import { Flex, IconButton, Icon, useToast } from '@chakra-ui/react';
import { Table } from '@tanstack/react-table';
import FileSaver from 'file-saver';
import { useTranslation } from 'helpers/languages/translations';
import GlobalFilter from 'plugins/DITable/filters/GlobalFilter';
import { EXCEL_FILE_TYPE } from 'shared/const';
import icons from 'shared/icons';
import { tableToolbarStyle, iconTableStyle } from 'shared/styles';
import { ICompanyInfo } from 'types';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';

type TAliasToolbarProps = {
    table: Table<any>;
    exportAliases: any;
    companyInfo?: ICompanyInfo;
    isExportLoading: boolean;
    onOpenAdd: any;
    readonly?: boolean;
};

const AliasToolbar = ({
    table,
    exportAliases,
    companyInfo,
    isExportLoading,
    onOpenAdd,
    readonly,
}: TAliasToolbarProps) => {
    const translate = useTranslation();
    const toast = useToast();

    const { setGlobalFilter } = table;

    const PlusIcon = icons.plus;
    const DownloadIcon = icons.download;

    const handleExportAlias = async () => {
        try {
            const { data: res, isError: isExportError } = await exportAliases({ isTemplate: false });
            if (isExportError || !res) throw new Error(translate('alias.export.error') as string);

            const blob = new Blob([res], {
                type: EXCEL_FILE_TYPE,
            });

            FileSaver.saveAs(blob, translate('alias.export.fileName', { name: companyInfo!.name_id }) as string);
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    return (
        <Flex {...tableToolbarStyle} justifyContent="space-between">
            <Flex>
                <GlobalFilter globalFilter={table.getState().globalFilter} setGlobalFilter={setGlobalFilter} />
                <IconButton
                    {...iconTableStyle}
                    onClick={intentionalyFloatingPromiseReturn(handleExportAlias)}
                    aria-label="download excel"
                    isLoading={isExportLoading}
                    size="sm"
                    icon={<DownloadIcon />}
                />
                <IconButton
                    {...iconTableStyle}
                    aria-label="add alias"
                    size="sm"
                    icon={<Icon as={PlusIcon} />}
                    onClick={onOpenAdd}
                    isDisabled={readonly}
                />
            </Flex>
        </Flex>
    );
};

export default AliasToolbar;
